import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { ScreenHeader, Surface, Button, IconButton, Avatar, Divider, Stat, Icon, Badge } from "../components/ui";

const API_URL = process.env.REACT_APP_API_URL;

const WEEK_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const WEEK_FULL   = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
const MONTH_NAMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

// Retorna um Date representando o momento atual no fuso de Recife (UTC-3, sem horário de verão)
function nowRecife() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Recife' }));
}

function toDateStr(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toTimeStr(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ── Cores ─────────────────────────────────────────────────────
const PALETTE = [
  { bg: '#fecaca', text: '#991b1b' },
  { bg: '#fed7aa', text: '#c2410c' },
  { bg: '#fef08a', text: '#a16207' },
  { bg: '#d9f99d', text: '#4d7c0f' },
  { bg: '#bbf7d0', text: '#15803d' },
  { bg: '#a5f3fc', text: '#0e7490' },
  { bg: '#bfdbfe', text: '#1d4ed8' },
  { bg: '#ddd6fe', text: '#6d28d9' },
  { bg: '#f5d0fe', text: '#a21caf' },
];
function studentColorIndex(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return Math.abs(h) % PALETTE.length;
}
function noRepeatColors(events) {
  const idxs = events.map(ev => studentColorIndex(ev.aluno || ''));
  for (let i = 1; i < idxs.length; i++) {
    let tries = 0;
    while (idxs[i] === idxs[i - 1] && tries < PALETTE.length - 1) {
      idxs[i] = (idxs[i] + 1) % PALETTE.length;
      tries++;
    }
  }
  return idxs.map(idx => PALETTE[idx]);
}

// ── Helpers de calendário ─────────────────────────────────────
function weekOf(base) {
  const dow = base.getDay();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() - dow + i);
    return d;
  });
}

// ── Grid semanal (somente leitura, semana atual) ───────────────
const PX_PER_MIN  = 0.9;
const GRID_START  = 7 * 60;
const GRID_END    = 22 * 60;

function timeToMin(hm) {
  const [h, m] = hm.split(':').map(Number);
  return h * 60 + m;
}
function minToHHMM(min) {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

function HomeWeekView({ agendamentos, todayStr, nowHM, onOpen }) {
  const today = nowRecife();
  const days  = weekOf(today);
  const byDate = {};
  agendamentos.forEach(a => { (byDate[a.data] = byDate[a.data] || []).push(a); });

  const byDateColors = {};
  days.forEach(d => {
    const ds = toDateStr(d);
    byDateColors[ds] = noRepeatColors(byDate[ds] || []);
  });

  let rangeStart = GRID_END, rangeEnd = GRID_START;
  days.forEach(d => {
    const dateStr = toDateStr(d);
    (byDate[dateStr] || []).forEach(ev => {
      const s = timeToMin(ev.hora_inicio);
      const e = s + ev.duracao;
      if (s < rangeStart) rangeStart = s;
      if (e > rangeEnd)   rangeEnd   = e;
    });
  });
  if (rangeStart >= rangeEnd) { rangeStart = 8 * 60; rangeEnd = 18 * 60; }
  rangeStart = Math.max(GRID_START, Math.floor(rangeStart / 60) * 60 - 30);
  rangeEnd   = Math.min(GRID_END,   Math.ceil(rangeEnd   / 60) * 60 + 30);
  const totalH = (rangeEnd - rangeStart) * PX_PER_MIN;

  const hours = [];
  for (let m = rangeStart; m < rangeEnd; m += 60) hours.push(m);

  const TIME_W = 34;

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', marginLeft: TIME_W }}>
        {days.map((d, i) => {
          const dateStr = toDateStr(d);
          const isToday = dateStr === todayStr;
          return (
            <div key={i} style={{ flex: 1, textAlign: 'center', paddingBottom: 6,
              borderBottom: isToday ? '2px solid var(--accent)' : '1px solid var(--border-hairline)' }}>
              <div style={{ fontSize: 9, color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase' }}>{WEEK_FULL[i]}</div>
              <div style={{ fontSize: 14, fontWeight: 700,
                color: isToday ? 'var(--accent)' : 'var(--text-primary)',
                fontFamily: '"Geist Mono", monospace' }}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', marginTop: 4 }}>
        <div style={{ width: TIME_W, flexShrink: 0, position: 'relative', height: totalH }}>
          {hours.map(m => (
            <div key={m} style={{ position: 'absolute', top: (m - rangeStart) * PX_PER_MIN, width: '100%', textAlign: 'right', paddingRight: 6 }}>
              <span style={{ fontSize: 9, color: 'var(--text-tertiary)', fontFamily: '"Geist Mono", monospace',
                lineHeight: 1, transform: 'translateY(-50%)', display: 'inline-block' }}>
                {minToHHMM(m)}
              </span>
            </div>
          ))}
        </div>

        {days.map((d, i) => {
          const dateStr = toDateStr(d);
          const isToday = dateStr === todayStr;
          const events  = byDate[dateStr] || [];
          return (
            <div key={i} style={{ flex: 1, position: 'relative', height: totalH,
              borderLeft: '1px solid var(--border-hairline)',
              background: isToday ? 'color-mix(in oklch, var(--accent) 4%, transparent)' : 'transparent' }}>

              {hours.map(m => (
                <div key={m} style={{ position: 'absolute', top: (m - rangeStart) * PX_PER_MIN,
                  left: 0, right: 0, borderTop: '1px solid var(--border-hairline)' }}/>
              ))}

              {isToday && (() => {
                const nowMin = timeToMin(nowHM);
                if (nowMin >= rangeStart && nowMin <= rangeEnd) return (
                  <div style={{ position: 'absolute', top: (nowMin - rangeStart) * PX_PER_MIN,
                    left: 0, right: 0, borderTop: '2px solid var(--accent)', zIndex: 10 }}>
                    <div style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--accent)',
                      position: 'absolute', left: -4, top: -4 }}/>
                  </div>
                );
                return null;
              })()}

              {events.map((ev, j) => {
                const col      = byDateColors[dateStr][j];
                const startMin = timeToMin(ev.hora_inicio);
                const top      = (startMin - rangeStart) * PX_PER_MIN;
                const height   = Math.max(ev.duracao * PX_PER_MIN, 20);
                const nome     = (ev.aluno || 'Aula').split(' ')[0];
                return (
                  <button key={j} onClick={() => onOpen && onOpen(ev)} title={`${ev.hora_inicio} · ${ev.aluno}`}
                    style={{
                      position: 'absolute', top, left: 1, right: 1, height,
                      background: col.bg, color: col.text,
                      border: 'none', borderLeft: `3px solid ${col.text}`,
                      borderRadius: 3, padding: '2px 3px',
                      cursor: onOpen ? 'pointer' : 'default', textAlign: 'left', overflow: 'hidden',
                      zIndex: 5,
                    }}>
                    <div style={{ fontSize: 9, fontWeight: 700, lineHeight: 1.3,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {nome}
                    </div>
                    {height >= 36 && (
                      <div style={{ fontSize: 8, opacity: 0.75, lineHeight: 1.3 }}>{ev.hora_inicio}</div>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function todayLabel() {
  const d = nowRecife();
  const days = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
  const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
}

function buildCalendar(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

function MiniCalendar({ agendamentos }) {
  const today = nowRecife();
  const year = today.getFullYear();
  const month = today.getMonth();
  const todayStr = toDateStr(today);
  const cells = buildCalendar(year, month);

  const upcomingDates = new Set(
    agendamentos
      .filter(a => a.status === 'aprovado' && a.data >= todayStr)
      .map(a => a.data)
  );

  return (
    <Surface padded>
      <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: -0.2, color: 'var(--text-primary)', marginBottom: 10, textTransform: 'capitalize' }}>
        {MONTH_NAMES[month].toLowerCase()} {year}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {WEEK_LABELS.map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', letterSpacing: 0.3 }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={i} style={{ height: 32 }}/>;
          const dateStr = `${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          const isToday = dateStr === todayStr;
          const hasClass = upcomingDates.has(dateStr);
          const isPast = dateStr < todayStr;
          return (
            <div key={i} style={{
              height: 32, borderRadius: 6,
              background: isToday ? 'var(--text-primary)' : hasClass ? 'var(--accent-soft)' : 'transparent',
              color: isToday ? 'var(--text-inverse)' : isPast ? 'var(--text-tertiary)' : hasClass ? 'var(--accent)' : 'var(--text-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: '"Geist Mono", monospace', fontSize: 12,
              fontWeight: isToday || hasClass ? 600 : 400,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {d}
            </div>
          );
        })}
      </div>
    </Surface>
  );
}

// ── Professor Dashboard ────────────────────────────────────────
function HomeProf({ nome, agendamentos }) {
  const navigate = useNavigate();

  const now = nowRecife();
  const todayStr = toDateStr(now);
  const nowTimeStr = toTimeStr(now);

  const todayClasses = agendamentos
    .filter(a => a.data === todayStr && a.status === 'aprovado')
    .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));

  const nextClass =
    todayClasses.find(a => a.hora_inicio > nowTimeStr) ||
    agendamentos
      .filter(a => a.data > todayStr && a.status === 'aprovado')
      .sort((a, b) => a.data.localeCompare(b.data) || a.hora_inicio.localeCompare(b.hora_inicio))[0] ||
    null;

  // Count classes this month
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const monthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-31`;
  const monthClasses = agendamentos.filter(a =>
    a.status === 'aprovado' && a.data >= monthStart && a.data <= monthEnd
  );

  // Aulas na semana (dom → sáb)
  const dow = now.getDay();
  const weekStart = toDateStr(new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow));
  const weekEnd   = toDateStr(new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow + 6));
  const weekClasses = agendamentos.filter(a =>
    a.status === 'aprovado' && a.data >= weekStart && a.data <= weekEnd
  );

  return (
    <div className="mema-screen mema-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)', paddingBottom: 90, overflow: 'auto' }}>
      <ScreenHeader title="Início" action={
        <IconButton icon="bell" variant="surface" size={36} label="Notificações"/>
      }/>

      {/* Greeting */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <div style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {todayLabel()}
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: -1, lineHeight: 1.1, margin: '4px 0 0', color: 'var(--text-primary)' }}>
          Olá, {nome.split(' ')[0]}.
        </h1>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4 }}>
          {todayClasses.length > 0
            ? <>Você tem <strong style={{ color: 'var(--text-primary)' }}>{todayClasses.length} aula{todayClasses.length > 1 ? 's' : ''}</strong> hoje.</>
            : 'Nenhuma aula agendada para hoje.'}
        </div>
      </div>

      {/* Next class hero */}
      {nextClass && (
        <div style={{ padding: '0 var(--gap-3)' }}>
          <Surface style={{
            padding: 'var(--gap-4)',
            background: 'linear-gradient(155deg, var(--accent), color-mix(in oklch, var(--accent) 70%, black))',
            border: 'none', color: '#fff',
            boxShadow: '0 1px 0 rgba(255,255,255,.15) inset, 0 12px 32px color-mix(in oklch, var(--accent) 40%, transparent)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
                padding: '3px 8px', background: 'rgba(255,255,255,0.18)', borderRadius: 999,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: 3, background: '#fff', animation: 'pulse 1.6s infinite' }}/>
                {nextClass.data === todayStr
                  ? 'Próxima aula'
                  : `Próxima aula · ${new Date(nextClass.data + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })}`}
              </span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.6, lineHeight: 1.15 }}>
              {nextClass.aluno}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, fontSize: 12.5, opacity: 0.9 }}>
              <span style={{ fontFamily: '"Geist Mono", monospace' }}>{nextClass.hora_inicio}</span>
              <span style={{ opacity: 0.5 }}>·</span>
              <span>{nextClass.duracao} min</span>
              <span style={{ opacity: 0.5 }}>·</span>
              <span>{nextClass.tipo || 'Presencial'}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <Button size="sm" onClick={() => navigate('/aulas')} style={{
                background: '#fff', color: 'var(--text-primary)', border: 'none',
              }}>Ver agenda</Button>
            </div>
          </Surface>
        </div>
      )}

      {/* Monthly stats */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'var(--gap-2)' }}>
          <h2 style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: 0 }}>
            {nowRecife().toLocaleString('pt-BR', { month: 'long' })} · resumo
          </h2>
          <button onClick={() => navigate('/relatorio')} style={{
            fontSize: 12, color: 'var(--text-secondary)', background: 'none', border: 'none',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: 'inherit',
          }}>
            Ver relatório <Icon name="chevron" size={12}/>
          </button>
        </div>
        <Surface padded style={{ display: 'flex', gap: 0 }}>
          <div style={{ flex: 1 }}>
            <Stat label="Aulas no mês" value={String(monthClasses.length)}/>
          </div>
          <Divider vertical style={{ margin: '0 var(--gap-2)' }}/>
          <div style={{ flex: 1 }}>
            <Stat label="Na semana" value={String(weekClasses.length)}/>
          </div>
          <Divider vertical style={{ margin: '0 var(--gap-2)' }}/>
          <div style={{ flex: 1 }}>
            <Stat label="Hoje" value={String(todayClasses.length)}/>
          </div>
        </Surface>
      </div>

      {/* Today's schedule */}
      {todayClasses.length > 0 && (
        <div style={{ padding: '0 var(--gap-3)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'var(--gap-2)' }}>
            <h2 style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: 0 }}>Agenda · hoje</h2>
            <button onClick={() => navigate('/aulas')} style={{
              fontSize: 12, color: 'var(--text-secondary)', background: 'none', border: 'none',
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: 'inherit',
            }}>
              Ver agenda <Icon name="chevron" size={12}/>
            </button>
          </div>
          <Surface>
            {todayClasses.map((c, i) => (
              <React.Fragment key={i}>
                {i > 0 && <Divider/>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-2)', padding: 'var(--pad-card)' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 52, gap: 2 }}>
                    <span style={{
                      fontFamily: '"Geist Mono", monospace', fontSize: 14, fontWeight: 540,
                      color: 'var(--text-primary)', letterSpacing: -0.3, fontVariantNumeric: 'tabular-nums',
                    }}>{c.hora_inicio}</span>
                    <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{c.duracao}min</span>
                  </div>
                  <Avatar name={c.aluno} size={32}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 540, color: 'var(--text-primary)', letterSpacing: -0.2,
                      whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{c.aluno}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)',
                      whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{c.tipo || 'Presencial'}</div>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </Surface>
        </div>
      )}

      {/* Weekly calendar */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'var(--gap-2)' }}>
          <h2 style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: 0 }}>
            Semana
          </h2>
          <button onClick={() => navigate('/aulas')} style={{
            fontSize: 12, color: 'var(--text-secondary)', background: 'none', border: 'none',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: 'inherit',
          }}>
            Ver agenda <Icon name="chevron" size={12}/>
          </button>
        </div>
        <Surface padded>
          <HomeWeekView agendamentos={agendamentos} todayStr={todayStr} nowHM={nowTimeStr}
            onOpen={null}/>
        </Surface>
      </div>
    </div>
  );
}

// ── Aluno Dashboard ────────────────────────────────────────────
function HomeAluno({ nome, perfil, agendamentos }) {
  const navigate = useNavigate();

  const nowAluno = nowRecife();
  const todayStr = toDateStr(nowAluno);
  const nowTimeStrAluno = toTimeStr(nowAluno);

  const upcoming = agendamentos
    .filter(a => a.status === 'aprovado' && (
      a.data > todayStr || (a.data === todayStr && a.hora_inicio > nowTimeStrAluno)
    ))
    .sort((a, b) => a.data.localeCompare(b.data) || a.hora_inicio.localeCompare(b.hora_inicio));

  const next3 = upcoming.slice(0, 3);

  const avatarSrc = perfil?.foto_url ? `${API_URL}${perfil.foto_url}` : null;

  return (
    <div className="mema-screen mema-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)', paddingBottom: 90, overflow: 'auto' }}>
      <ScreenHeader title="Início"/>

      {/* Perfil + saudação */}
      <div style={{ padding: '0 var(--gap-3)', display: 'flex', alignItems: 'center', gap: 'var(--gap-3)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {todayLabel()}
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: -1, lineHeight: 1.1, margin: '4px 0 0', color: 'var(--text-primary)' }}>
            Oi, {nome.split(' ')[0]}.
          </h1>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4 }}>
            {next3.length > 0
              ? next3[0].data === todayStr
                ? <>Sua aula é <strong style={{ color: 'var(--text-primary)' }}>hoje às {next3[0].hora_inicio}</strong>.</>
                : <>Próxima aula em <strong style={{ color: 'var(--text-primary)' }}>{new Date(next3[0].data + 'T00:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })}</strong>.</>
              : 'Nenhuma aula agendada.'}
          </div>
        </div>
        <button onClick={() => navigate('/meu-perfil')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }}>
          {avatarSrc
            ? <img src={avatarSrc} alt={nome} style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover',
                boxShadow: '0 0 0 2px var(--bg-surface), 0 0 0 3.5px var(--accent)' }}/>
            : <Avatar name={nome} size={56} ring/>
          }
        </button>
      </div>

      {/* Próximas aulas */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'var(--gap-2)' }}>
          <h2 style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: 0 }}>
            Próximas aulas
          </h2>
          <button onClick={() => navigate('/aulas')} style={{
            fontSize: 12, color: 'var(--text-secondary)', background: 'none', border: 'none',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: 'inherit',
          }}>
            Ver agenda <Icon name="chevron" size={12}/>
          </button>
        </div>

        {next3.length === 0 ? (
          <Surface padded style={{ textAlign: 'center' }}>
            <Icon name="calendar" size={26} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.35, color: 'var(--text-tertiary)' }}/>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>Nenhuma aula agendada.</div>
            <Button size="sm" variant="secondary" icon="plus" style={{ marginTop: 12 }}
              onClick={() => navigate('/horarios-disponiveis')}>
              Solicitar aula
            </Button>
          </Surface>
        ) : (
          <Surface>
            {next3.map((c, i) => {
              const isToday = c.data === todayStr;
              const dateLabel = isToday
                ? 'Hoje'
                : new Date(c.data + 'T00:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
              return (
                <React.Fragment key={i}>
                  {i > 0 && <Divider/>}
                  <div style={{ display: 'flex', gap: 'var(--gap-2)', padding: 'var(--pad-card)', position: 'relative' }}>
                    {/* Time rail */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 52 }}>
                      <span style={{
                        fontFamily: '"Geist Mono", monospace', fontSize: 14, fontWeight: 540,
                        color: isToday ? 'var(--accent)' : 'var(--text-primary)', letterSpacing: -0.3,
                        fontVariantNumeric: 'tabular-nums',
                      }}>{c.hora_inicio}</span>
                      <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{dateLabel}</span>
                    </div>
                    {/* Connector dot + line */}
                    <div style={{ position: 'relative', width: 12, flexShrink: 0 }}>
                      <div style={{
                        position: 'absolute', top: 6, left: 5, width: 6, height: 6, borderRadius: 3,
                        background: isToday ? 'var(--accent)' : i === 0 ? 'var(--text-primary)' : 'var(--border-strong)',
                        boxShadow: isToday ? '0 0 0 4px var(--accent-soft)' : 'none',
                      }}/>
                      {i < next3.length - 1 && (
                        <div style={{ position: 'absolute', top: 16, bottom: -16, left: 7.5, width: 1, background: 'var(--border-hairline)' }}/>
                      )}
                    </div>
                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', letterSpacing: -0.2 }}>
                        Aula com Mema
                        {isToday && <Badge tone="accent" style={{ marginLeft: 6 }}>Hoje</Badge>}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <Icon name={c.tipo === 'online' ? 'cam' : 'location'} size={11}/>
                        {c.tipo || 'Presencial'} · {c.duracao} min
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </Surface>
        )}
      </div>

      {/* Mini calendário */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <h2 style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '0 0 var(--gap-2)' }}>
          Calendário
        </h2>
        <MiniCalendar agendamentos={agendamentos}/>
      </div>

      {/* Meu perfil card */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <Surface>
          <button onClick={() => navigate('/meu-perfil')} style={{
            display: 'flex', alignItems: 'center', gap: 'var(--gap-2)', padding: 'var(--pad-card)',
            width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
          }}>
            {avatarSrc
              ? <img src={avatarSrc} alt={nome} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover',
                  boxShadow: '0 0 0 2px var(--bg-surface), 0 0 0 2.5px var(--accent)', flexShrink: 0 }}/>
              : <Avatar name={nome} size={40} ring/>
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', letterSpacing: -0.2,
                whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{nome}</div>
              <Badge tone="accent" style={{ marginTop: 3 }}>Aluno</Badge>
            </div>
            <Icon name="chevron" size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}/>
          </button>
        </Surface>
      </div>
    </div>
  );
}

// ── Root Home ─────────────────────────────────────────────────
export default function Home() {
  const { tipo } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/perfil`, { withCredentials: true }),
      axios.get(`${API_URL}/agendamentos`, { withCredentials: true }),
    ])
      .then(([perfilRes, agRes]) => {
        setPerfil(perfilRes.data);
        setAgendamentos(agRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tipo]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>Carregando…</div>
      </div>
    );
  }

  const nome = perfil?.nome || (tipo === 'professora' ? 'Professora' : 'Aluno');

  if (tipo === 'aluno') {
    return <HomeAluno nome={nome} perfil={perfil} agendamentos={agendamentos}/>;
  }
  return <HomeProf nome={nome} agendamentos={agendamentos}/>;
}
